<template>
  <div class="d-flex flex-column flex-md-row">
    <!-- Left Sidebar for Filters -->
    <div class="ecom-sidebar-header border-r mb-2 mb-md-0">
      <div class="d-flex align-center justify-space-between px-4 pb-2 pt-1 border-b">
        <span
          class="text-subtitle-1 font-weight-bold black--text text-uppercase"
          style="letter-spacing: 1px"
          >FILTERS</span
        >
        <span
          class="text-caption font-weight-bold cursor-pointer primary--text"
          style="letter-spacing: 0.5px"
          @click="clearAllFilters"
        >
          CLEAR ALL
        </span>
      </div>

      <div class="pt-2 ecom-sidebar">
        <EcomFilterCategory
          v-if="!isLoanManagement && users && users.length > 0"
          title="Assignee"
          v-model="filterAssignedTo"
          :items="[{ fullName: 'Unassigned', _id: 'null' }].concat(users || [])"
          itemText="fullName"
          itemValue="_id"
          :searchable="true"
          @change="handleChange"
        />
        <EcomFilterCategory
          v-if="
            !isLoanManagement &&
            userDefinedOutcomeOptions &&
            userDefinedOutcomeOptions.length > 0 &&
            (!caseConfig?.baseFieldMappings?.outcome ||
              caseConfig.baseFieldMappings.outcome.tableVisibility !== false)
          "
          :title="outcomeTitle"
          itemText="text"
          itemValue="value"
          v-model="filterCaseOutcome"
          :items="userDefinedOutcomeOptions"
          :searchable="true"
          @change="handleNoOutcome"
        />
        <EcomFilterCategory
          v-if="branches && branches.length > 0"
          v-show="isBranchFeatureVisible('ASSIGN_BRANCH_TO_ENTITY')"
          title="Branch"
          v-model="filterBranch"
          :items="branches"
          itemText="branchName"
          itemValue="_id"
          :searchable="true"
          @change="handleBranchChange"
        />
        <EcomFilterCategory
          v-if="computedTeams && computedTeams.length > 0"
          v-show="isBranchFeatureVisible('ASSIGN_BRANCH_TO_ENTITY')"
          title="Team"
          v-model="filterTeam"
          :items="computedTeams"
          itemText="teamName"
          itemValue="_id"
          :searchable="true"
          :disabled="!filterBranch"
          :itemDisabled="isTeamDisabled"
          @change="handleChange"
        />
        <EcomFilterCategory
          v-if="isEntityEnabled && entities && entities.length > 0"
          :title="entityTitle || 'Entity'"
          v-model="entityId"
          :items="entities"
          itemText="entityName"
          itemValue="_id"
          :searchable="true"
          @change="handleChange"
        />

        <EcomFilterCategory
          v-if="emailCategories && emailCategories.length > 0"
          title="Email Category"
          v-model="filterTag"
          :items="emailCategories"
          itemText="text"
          itemValue="value"
          @change="handleChange"
        />

        <EcomFilterCategory
          v-if="!isLoanManagement && caseStatusOptions && caseStatusOptions.length > 0"
          :title="statusTitle"
          v-model="filterStatus"
          :items="caseStatusOptions"
          itemText="label"
          itemValue="value"
          @change="handleChange"
        />
        <EcomFilterCategory
          v-if="isLoanManagement && loanStatusOptions && loanStatusOptions.length > 0"
          title="Loan Status"
          v-model="filterLoanStatus"
          :items="loanStatusOptions"
          itemText="label"
          itemValue="value"
          @change="handleChange"
        />
        <EcomFilterCategory title="Date Range">
          <v-menu
            v-model="menu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                class="text-caption mt-1"
                placeholder="Select Date Range"
                v-model="formattedDate"
                prepend-inner-icon="mdi-calendar"
                readonly
                hide-details
                outlined
                dense
                clearable
                v-on="on"
                @click:clear="onDateCleared"
                background-color="#f5f5f6"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="dates"
              @input="onDateSelected"
              no-title
              @change="menu = false"
              range
              scrollable
              color="primary"
            />
          </v-menu>
        </EcomFilterCategory>
      </div>
    </div>

    <div class="ecom-table-container flex-grow-1 pl-md-4">
      <EmailTimelineTable
        v-if="isEmailTimelineEnabled"
        :cases="cases"
        :loading="loadTable"
        :totalItems="totalItems"
        v-model="selected"
        :users="users"
        :caseConfig="caseConfig"
        :subCases="subCases"
        :caseStatusOptions="caseStatusOptions"
        :canEdit="canEdit"
        :canWrite="canWrite"
        :canDelete="canDelete"
        :isLoanManagement="isLoanManagement"
        :tabText="tabText"
        :filterParams="filterParams()"
        @case-details="caseDetails"
        @edit-case="casesEdit"
        @add-task="addCaseTask"
        @delete-case="handleDelete"
        @update:options="
          (newOpts) => {
            options = { ...options, ...newOpts }
          }
        "
        @refresh="fetchCases"
      />
      <v-data-table
        v-else
        :headers="computedHeaders"
        :items="cases"
        class="text-sm table-radius border border-[#e0e0e0]"
        :footer-props="{ itemsPerPageOptions: [5, 10, 15, 30, 50] }"
        :options.sync="options"
        :server-items-length="totalItems"
        :loading="loadTable"
        fixed-header
        show-select
        item-key="caseNo"
        v-model="selected"
        :item-class="(item) => (item.isDeleted ? 'bg-red-50/30 opacity-60' : '')"
      >
        <!-- Header tooltips implementation -->
        <template v-for="(header, index) in computedHeaders" v-slot:[`header.${header.value}`]>
          <p
            :key="index"
            class="font-title"
            v-if="header.text?.length > 15"
            v-tooltip="{ text: header.text }"
          >
            {{ header.text }}
          </p>
          <p v-else class="font-title">{{ header.text }}</p>
        </template>

        <template v-slot:[`item.caseNo`]="{ item }">
          <td
            class="px-4 py-3 table-font hover:text-blue-700 dark-hover-text cursor-pointer font-bold primary--text text-lg transition-all duration-200"
            :class="{
              'hover:underline': !item.isDeleted,
              'text-red-600 line-through opacity-60': item.isDeleted
            }"
            @click="caseDetails(item)"
          >
            {{ item.caseNo }}
            <span v-if="item.loanDetails?._id">({{ item.loanDetails?.emiSchedule?.length }})</span>
            <span v-else>({{ item.tasks?.length }})</span>
            <div v-if="item.isDeleted">
              <v-chip x-small color="red" dark label class="font-bold uppercase py-0 px-1 mt-1"
                >Deleted</v-chip
              >
            </div>
            <div v-else>
              <!-- 
              <v-chip
                v-if="isLoanManagement"
                small
                outlined
                class="font-bold"
                :color="getLoanCompletionColor(item.loanDetails?.loanWorkflow)"
              >
                Completed - {{ loanCompletedStatus(item.loanDetails?.loanWorkflow) || '-' }}
              </v-chip>
               -->

              <!-- color="blue-grey darken-3"
                text-color="white" -->
              <v-chip v-if="isLoanManagement" small label class="font-bold whitespace-nowrap">
                {{ item.loanDetails?.currentStatus || item.status }}
              </v-chip>
            </div>
          </td>
        </template>
        <template v-slot:[`item.customer`]="{ item }">
          <td v-if="isEmailTimelineEnabled" class="px-4 py-3 table-font min-w-[200px]">
            <v-menu
              v-if="activeEditingCaseId === item._id"
              v-model="showCustomerMenu"
              offset-y
              max-height="300"
              :close-on-content-click="false"
              min-width="260"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="customerSearchInput"
                  dense
                  outlined
                  hide-details
                  autofocus
                  placeholder="Search by Name, CRN or Case No"
                  :loading="loadingCustomers"
                  v-bind="attrs"
                  v-on="on"
                  @input="debounceSearchCustomer"
                  append-icon="mdi-close"
                  @click:append="activeEditingCaseId = null"
                ></v-text-field>
              </template>
              <v-card class="rounded-md shadow-lg border">
                <v-list v-if="customersSearchList.length > 0" dense class="py-0">
                  <v-list-item
                    v-for="opt in customersSearchList"
                    :key="opt.optionId"
                    @click="updateCaseCustomer(item, opt)"
                    class="hover:bg-blue-50 cursor-pointer"
                  >
                    <v-list-item-content>
                      <v-list-item-title class="font-bold text-sm">
                        {{ opt.customerName }}
                      </v-list-item-title>
                      <v-list-item-subtitle v-if="opt.caseNo" class="text-caption grey--text">
                        Case No: {{ opt.caseNo }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
                <div v-else class="px-4 py-3 text-caption grey--text text-center">
                  {{ loadingCustomers ? 'Searching...' : 'Type to search customer...' }}
                </div>
              </v-card>
            </v-menu>
            <div
              v-else
              class="cursor-pointer flex justify-between text-blue-600 font-semibold hover:text-blue-800 transition-all duration-200"
              @click="startEditingCustomer(item)"
            >
              {{ item.customer?.customerFullName || 'Link Customer' }}
              <v-icon small class="ml-1 text-gray-400">mdi-pencil</v-icon>
            </div>
          </td>
          <td
            v-else
            class="px-4 py-3 table-font cursor-pointer text-blue-600 font-semibold hover:text-blue-800 transition-all duration-200"
            :class="{ 'hover:underline': !item.isDeleted, 'opacity-60': item.isDeleted }"
            @click="routeToCustomer(item)"
          >
            <span :class="{ 'line-through text-gray-500': item.isDeleted }">
              {{ item.customer?.customerFullName || '-' }}
            </span>
          </td>
        </template>
        <template v-slot:[`item.emi(s)`]="{ item }">
          <td class="table-font px-4 py-3">
            <div class="d-flex align-center">
              <div>
                <p class="mb-0">
                  <span :style="{ color: statusColor('pending').textColor }">Pending - </span>
                  <span>{{ totalEMI(item.loanDetails?.emiSchedule)?.pending || '0' }}</span>
                </p>
                <v-divider class="my-1"></v-divider>
                <p class="mb-0">
                  <span :style="{ color: statusColor('paid').textColor }">Paid - </span>
                  <span>{{ totalEMI(item.loanDetails?.emiSchedule)?.paid || '0' }}</span>
                </p>
              </div>
              <v-spacer></v-spacer>
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on" small>
                    <v-icon size="20">mdi-chevron-down</v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>
                        <span :style="{ color: statusColor('late_paid').textColor }"
                          >Late Paid -
                        </span>
                        <span>{{ totalEMI(item.loanDetails?.emiSchedule)?.latePaid || '0' }}</span>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <!-- <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>
                        <span :style="{ color: statusColor('late_paid').textColor }"
                          >Late Paid -
                        </span>
                        <span>
                          {{
                            lateFeeCalculation(
                              item.loanDetails?.emiSchedule
                            )?.latePaidCount?.toLocaleString('en-IN') || '0'
                          }}
                        </span>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item> -->
                </v-list>
              </v-menu>
            </div>
          </td>
        </template>

        <template v-slot:[`item.disbursements`]="{ item }">
          <td class="px-4 py-3 table-font">
            <div
              v-if="
                item.loanDetails?.loanDisbursements && item.loanDetails.loanDisbursements.length > 0
              "
            >
              <div
                v-for="(disbursement, index) in formatDisbursementDetails(
                  item.loanDetails.loanDisbursements
                )"
                :key="index"
                class="disbursement-row mb-2"
              >
                <div class="d-flex align-center">
                  <span class="disbursement-amount"
                    >₹{{ disbursement.amount?.toLocaleString('en-IN') }}</span
                  >
                  <span class="disbursement-date mx-1">({{ disbursement.date }})</span>
                </div>
                <v-chip class="mt-1" small outlined :color="getStatusColor(disbursement.status)">
                  {{ disbursement.status }}
                </v-chip>
                <v-divider
                  v-if="
                    index < formatDisbursementDetails(item.loanDetails.loanDisbursements).length - 1
                  "
                  class="my-2"
                ></v-divider>
              </div>
            </div>
            <div v-else class="text-left text--secondary">No disbursements</div>
          </td>
        </template>
        <template v-slot:[`item.lender`]="{ item }">
          <td
            class="px-4 py-3 table-font cursor-pointer hover:text-blue-700 dark-hover-text"
            @click="routeToPartner(item)"
          >
            {{ item.loanDetails?.partner?.partnerName || '-' }}
          </td>
        </template>
        <template v-slot:[`item.principal`]="{ item }">
          <td class="px-4 py-3 table-font">
            <!-- ₹{{ outstandingAmt(item.loanDetails?.emiSchedule)?.toLocaleString('en-IN') || '-' }} -->
            <p class="mb-0">
              <span :style="{ color: statusColor('outstanding').textColor }">Outstanding - </span>
              <span
                >₹{{
                  emiAmtStats(
                    item.loanDetails?.emiSchedule
                  )?.totalPricipalOutstanding?.toLocaleString('en-IN') || '-'
                }}</span
              >
            </p>
            <v-divider class="my-2"></v-divider>
            <p>
              <span :style="{ color: statusColor('recovered').textColor }">Recovered - </span>
              <span>
                ₹{{
                  emiAmtStats(
                    item.loanDetails?.emiSchedule
                  )?.totalPrincipalRecovered?.toLocaleString('en-IN') || '-'
                }}</span
              >
            </p>
          </td>
        </template>
        <template v-slot:[`item.interest`]="{ item }">
          <td class="px-4 py-3 table-font">
            <!-- ₹{{ outstandingAmt(item.loanDetails?.emiSchedule)?.toLocaleString('en-IN') || '-' }} -->
            <p class="mb-0">
              <span :style="{ color: statusColor('expected').textColor }">Expected - </span>
              <span
                >₹{{
                  emiAmtStats(item.loanDetails?.emiSchedule)?.totalInterestExpected?.toLocaleString(
                    'en-IN'
                  ) || '-'
                }}</span
              >
            </p>
            <v-divider class="my-2"></v-divider>
            <p>
              <span :style="{ color: statusColor('recovered').textColor }">Recovered - </span>
              <span>
                ₹{{
                  emiAmtStats(
                    item.loanDetails?.emiSchedule
                  )?.totalInterestRecovered?.toLocaleString('en-IN') || '-'
                }}</span
              >
            </p>
          </td>
        </template>
        <template v-slot:[`item.latefee`]="{ item }">
          <td class="px-4 py-3 table-font">
            <p class="mb-0">
              <span :style="{ color: statusColor('pending').textColor }">Pending - </span>
              <span
                >₹{{
                  lateFeeCalculation(item.loanDetails?.emiSchedule)?.pendingLateFee?.toLocaleString(
                    'en-IN'
                  ) || '0'
                }}
              </span>
            </p>
            <v-divider class="my-2"></v-divider>
            <p>
              <span :style="{ color: statusColor('late_paid').textColor }">Paid - </span>
              <span>
                ₹{{
                  lateFeeCalculation(item.loanDetails?.emiSchedule)?.paidLateFee?.toLocaleString(
                    'en-IN'
                  ) || '0'
                }}
              </span>
            </p>
          </td>
        </template>
        <template v-slot:[`item.loanamount`]="{ item }">
          <td class="px-4 py-3 table-font">
            <p class="mb-0">
              <span :style="{ color: statusColor('requested').textColor }">Requested - </span>
              <span
                >₹{{ item.loanDetails?.loanAmountRequested?.toLocaleString('en-IN') || '-' }}</span
              >
            </p>
            <v-divider class="my-2"></v-divider>
            <p>
              <span :style="{ color: statusColor('sanctioned').textColor }">Sanctioned - </span>
              <span>
                ₹{{ item.loanDetails?.loanAmountSanctioned?.toLocaleString('en-IN') || '-' }}</span
              >
            </p>
          </td>
        </template>
        <template v-slot:[`item.typeOfLoan`]="{ item }">
          <td class="px-4 py-3 table-font">
            {{ convertTitle(item?.loanDetails?.typeOfLoan || item?.typeOfLoan) || '-' }}
          </td>
        </template>
        <template v-slot:[`item.loanCategory`]="{ item }">
          <td class="px-4 py-3 table-font">
            {{ convertTitle(item?.loanDetails?.loanCategory || item?.loanCategory) || '-' }}
          </td>
        </template>
        <template v-slot:[`item.leadSource`]="{ item }">
          <td class="px-4 py-3 table-font">
            {{ convertTitle(item?.leadSource) || '-' }}
          </td>
        </template>
        <template v-slot:[`item.bkt`]="{ item }">
          <td class="px-4 py-3 table-font">{{ extractValue(item.bkt) }}</td>
        </template>
        <template v-slot:[`item.pos`]="{ item }">
          <td class="px-4 py-3 table-font">{{ item.pos }}</td>
        </template>
        <template v-slot:[`item.distanceFromBranch`]="{ item }">
          <td v-if="getBranchDistance(item)" class="px-4 py-3 table-font">
            {{ getBranchDistance(item) }}
          </td>
          <td v-else class="px-4 py-3 table-font">
            <button class="btn btn-primary" @click="getDistanceFromBranch(item)">
              Calculate Distance
            </button>
          </td>
        </template>
        <template v-slot:[`item.entityName`]="{ item }">
          <td class="px-4 py-3 table-font">
            {{ item.entity?.entityName || '-' }}
          </td>
        </template>
        <template v-slot:[`item.emiAmount`]="{ item }">
          <td class="px-4 py-3 table-font">{{ item.emiAmount }}</td>
        </template>
        <template v-slot:[`item.tenure`]="{ item }">
          <td class="px-4 py-3 table-font">
            {{ item.tenure }}
          </td>
        </template>
        <template v-for="option in dynamicColumns" v-slot:[`item.${option.value}`]="{ item }">
          <td :key="option.value" class="px-4 py-3 table-font">
            {{
              item?.tasks[0] && item?.tasks[0].meetingDetails?.meetingOutCome === option?.value
                ? item?.tasks[0].meetingDetails?.fundCollected || '-'
                : '-'
            }}
          </td>
        </template>

        <template v-slot:item.drag>
          <div class="drag-handle cursor-move d-flex justify-center">
            <v-icon>mdi-drag</v-icon>
          </div>
        </template>

        <!-- <template v-slot:item.tat="{ item }">
          <td class="px-4 py-6 table-font">{{ calculateTAT(item) }}</td>
        </template>
        <template v-slot:item.caseStartDate="{ item }">
          <td class="px-4 py-6 table-font">
            {{ item.caseStartDate ? $dayjs(item.caseStartDate).format('DD/MM/YYYY') : '' }}
            <br />
            {{ item.caseStartDate ? $dayjs(item.caseStartDate).format('hh:mm A') : '-' }}
          </td>
        </template> -->
        <template v-slot:item.branchName="{ item }">
          <td class="px-4 py-3 table-font">{{ findBranch(item) }}</td>
        </template>
        <template v-slot:item.teamName="{ item }">
          <td class="px-4 py-3 table-font">{{ findTeam(item.team) }}</td>
        </template>
        <template v-slot:item.assignedTo="{ item }">
          <td class="px-4 py-3 table-font">
            <template>
              <v-autocomplete
                v-model="item.assignedTo"
                :items="users"
                item-text="fullName"
                item-value="_id"
                class="input-field"
                label="Assigned To"
                placeholder="Assigned To"
                hide-details
                flat
                solo
                multiple
                :disabled="item.status === 'CLOSED'"
                @change="(val) => handleAssignedToChange(val, item)"
              >
                <!-- v-tooltip="{ text: `${employeeFullName(item.assignedTo)}` }" -->
                <!-- <template v-slot:prepend-item>
                  <v-list-item
                    ripple
                    @mousedown.prevent
                    v-show="item.assignedTo"
                    @click="caseStatusUpdate('null', item, 'assignedTo')"
                    class="unassignedOptions"
                  >
                    <v-list-item-content>
                      <v-list-item-title class="py-0"> Unassigned </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider class=""></v-divider>
                </template> -->
                <template v-slot:item="{ item: userItem }">
                  <div class="d-flex align-center w-full" v-if="userItem._id !== 'null'">
                    <v-checkbox
                      :input-value="isAssigned(item.assignedTo, userItem._id)"
                      hide-details
                      class="mb-4"
                      readonly
                      style="pointer-events: none"
                    ></v-checkbox>
                    <UserAvatar
                      :user="userItem"
                      :size="24"
                      :show-name="true"
                      :name-class="'text-sm ml-2'"
                      :avatar-class="'employee-image'"
                      :show-employee-id="true"
                      :show-mask-details="false"
                      :tooltip-enabled="false"
                    />
                  </div>
                  <div class="d-flex align-center w-full" v-else>
                    <v-checkbox
                      :input-value="!item.assignedTo || item.assignedTo.length === 0"
                      hide-details
                      class="mb-4"
                      readonly
                      style="pointer-events: none"
                    ></v-checkbox>
                    <span class="ml-2 text-sm">Unassigned</span>
                  </div>
                </template>
              </v-autocomplete>
            </template>
          </td>
        </template>
        <template v-slot:item.outcome="{ item }">
          <td class="px-4 py-3 table-font">
            <v-select
              v-model="item.userDefinedCaseStatus"
              label="Outcome"
              :items="userDefinedStatusOptions"
              item-text="text"
              item-value="value"
              hide-details
              flat
              solo
              :disabled="item.status === 'CLOSED'"
              @change="(outcome) => caseStatusUpdate(outcome, item, 'userDefinedCaseStatus')"
            >
              <template v-slot:prepend-item>
                <v-list-item
                  ripple
                  @mousedown.prevent
                  v-show="item.userDefinedCaseStatus"
                  @click="caseStatusUpdate('null', item, 'userDefinedCaseStatus')"
                  class="noOutcomeOptions"
                >
                  <v-list-item-content>
                    <v-list-item-title class="py-0"> Reset </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider class=""></v-divider>
              </template>
            </v-select>
          </td>
        </template>
        <template v-slot:item.status="{ item }">
          <div class="d-flex flex-column px-4" style="width: fit-content">
            <OrderStatuses
              v-if="!isLoanManagement"
              :orderStatus="item.status"
              :statuses="caseStatusOptions"
              @statusChange="(status) => caseStatusUpdate(status, item, 'status')"
            />
          </div>
        </template>
        <template v-slot:item.action="{ item }">
          <td class="px-4 py-3">
            <action-button
              :item="item"
              :canEdit="canEdit"
              :canWrite="canWrite"
              :canDelete="canDelete"
              :handleEdit="() => casesEdit(item)"
              :addTaskAction="() => addCaseTask(item)"
              :handleDelete="() => handleDelete(item)"
              :showAddIcon="'mdi-plus'"
              :plusIconText="`Add ${tabText?.visitTable?.displayName || 'Visit'}`"
              :showEditButton="canEdit"
              :showAddTaskButton="canWrite && !isLoanManagement"
              :showDeleteButton="canDelete"
            />
          </td>
        </template>
      </v-data-table>
    </div>

    <v-dialog v-model="openEditCase" width="90rem" persistent v-if="isLoanManagement">
      <AddEditLoanManagement
        @caseAdded="handleAdded"
        @close="handleAdded"
        @emittedEntity="handleEntity"
        :editCase="editCase"
        :caseConfig="caseConfig"
        :entities="entities"
      />
    </v-dialog>
    <v-dialog v-model="openEditCase" width="auto" persistent v-else>
      <AddEditCase
        v-if="openEditCase"
        @caseAdded="handleAdded"
        @emittedEntity="handleEntity"
        @exportMappingFormat="exportXlsxFormat"
        :editCase="editCase"
        :caseConfig="caseConfig"
        :entities="entities"
      />
    </v-dialog>
    <v-dialog v-model="openUploadCases" max-width="600" persistent>
      <UploadCases
        @updateUploadData="handleAdded"
        @close="closeUpload"
        @downloadSample="exportXlsxFormat"
        :caseConfig="caseConfig"
        :caseAction="caseAction"
      />
    </v-dialog>
    <v-dialog v-model="openAddCaseTask" max-width="600" persistent>
      <AddEditCaseTask
        v-if="openAddCaseTask"
        @caseTaskAdded="handleAdded"
        :editCase="editCase"
        :caseConfig="caseConfig"
      />
    </v-dialog>
    <v-dialog v-model="openCaseDetails" max-width="98rem" persistent>
      <CaseDetails
        :selectedCase="selectedCase"
        :caseConfig="caseConfig"
        :branchName="findBranch(selectedCase)"
        @closeDialog="closeDialog"
        @select-case="(id) => handleSelectCaseById(id)"
        @update-selected-case="(caseData) => (selectedCase = caseData)"
      />
    </v-dialog>
    <v-dialog v-model="openCustomerDetails" max-width="1200" content-class="rounded-md">
      <CustomerDetails
        v-if="currentCustomer"
        :customer="currentCustomer"
        :closeCustomerPopUp="() => (openCustomerDetails = false)"
      />
    </v-dialog>
    <AlertPopUp
      :showConfirmationDialog.sync="showConfirmationDialog"
      :confirmationTitle="'Confirm Delete'"
      :confirmationMessage="'Are you sure you want to delete the case?'"
      :confirmationActionText="'DELETE'"
      :confirmationActionColor="'red'"
      :performAction="caseDelete"
      :cancel="cancel"
    />
    <AlertPopUp
      :showConfirmationDialog.sync="showUpdateVisitDialog"
      :confirmationTitle="'Confirm Update'"
      :confirmationMessage="`Do you want to change pending visits to ${newAssignedTo}?`"
      :confirmationActionText="'UPDATE'"
      :confirmationActionColor="'blue'"
      :performAction="updateVisit"
      :cancel="cancelVisitUpdate"
    />

    <AlertPopUp
      :showConfirmationDialog="showDeleteDialog"
      :confirmationTitle="'Confirm Delete'"
      :confirmationMessage="'Are you sure you want to delete cases?'"
      :confirmationCustomerMessage="'Do you want to delete associated customer with selected cases?'"
      :afterConfirmationMessage="`Please make sure customer is not linked with other cases which you don't want to delete.`"
      :confirmationActionText="'DELETE'"
      :confirmationActionColor="'red'"
      :performAction="casesDeleteFunc"
      :deleteCustomerFunc="handleCheckedUpdate"
      :cancel="closeDeleteDialog"
      :dialogLable="dialogLable"
      :loading="loading"
      :deleteCustomer="deleteCustomer"
    />

    <AlertPopUp
      :showConfirmationDialog="showDeleteAllDialog"
      :confirmationTitle="'Confirm Delete'"
      :confirmationMessage="'Are you sure you want to delete all cases? Deleted cases cannot be reverted.'"
      :confirmationActionText="'DELETE'"
      :confirmationActionColor="'red'"
      :performAction="deleteAllCases"
      :cancel="handleShowDialogDeleteAll"
      :dialogLable="dialogLable"
      :loading="loading"
    />

    <UpdateCases
      :visible.sync="showDialog"
      :handleShowDialog="handleShowDialog"
      :handleDeleteSelected="handleDeleteSelected"
      :items="selected"
      :users="users"
      :branches="branches"
      :userDefinedStatusOptions="userDefinedStatusOptions"
      :caseConfig="caseConfig"
      :dialogLable="dialogLable"
      :departments="departments"
      :designations="designations"
    >
    </UpdateCases>
    <ExportDynamicColumns
      :showExportDialog="exportDialog"
      :exportKeys="exportColumnHeaders"
      :exportLoading="exportLoading"
      :caseType="caseConfig.name"
      @closeExportDialog="closeExportDialog"
      @exportData="(selectedKeys) => exportCases(selectedKeys)"
    />

    <!-- Loan Bulk Upload Dialog -->
    <v-dialog v-model="openLoanBulkUploadDialog" max-width="1000" persistent>
      <LoanBulkUpload
        :caseConfig="caseConfig"
        @close="closeLoanBulkUpload"
        @uploadComplete="handleLoanUploadComplete"
      />
    </v-dialog>
    <FloatingActionBar
      v-if="!isLoanManagement"
      :selected="selected"
      :total-items="totalItems"
      :bulk-updating="false"
      :showUpdateOutcome="true"
      :showAssignSelection="true"
      :show-clear="false"
      :show-delete-selected="canDelete"
      :showDeleteAll="true"
      @select-all="handleShowDialogDeleteAll('delete')"
      @delete-selected="handleShowDialogDelete('delete')"
      @update-outcome="() => handleShowDialog(UpdateCaseDialogLabels.STATUS)"
      @assign-cases="() => handleShowDialog(UpdateCaseDialogLabels.FE_NAME)"
      @assign-location="handleAssignLocationClick"
      @assign-department="handleAssignDepartmentClick"
      @delete-all="handleShowDialogDeleteAll('delete')"
    />
    <FloatingActionBar
      v-else
      :selected="selected"
      :total-items="totalItems"
      :bulk-updating="false"
      :showUpdateOutcome="false"
      :showAssignSelection="false"
      :show-branch="false"
      :show-clear="false"
      :show-delete-selected="canDelete"
      @delete-selected="handleShowDialogDelete('delete')"
      @delete-all="handleShowDialogDeleteAll('delete')"
      @assign-location="handleAssignLocationClick"
      @assign-department="handleAssignDepartmentClick"
      :showDeleteAll="true"
    />
  </div>
</template>
<script>
import EcomFilterCategory from '@/components/EcomFilterCategory.vue'
import ActionButton from '@/components/ActionButton'
import AddEditCase from './AddEditCase'
import AddEditLoanManagement from './AddEditLoanManagement'
import CaseDetails from '../CaseDetails'
import UploadCases from './UploadCases'
import AddEditCaseTask from '../AddEditCaseTask'
import AlertPopUp from '@/components/AlertPopUp'
import UserAvatar from '@/components/UserAvatar.vue'
import OrderStatuses from '../../InventoryManagement/OrderStatuses.vue'
import LoanBulkUpload from './LoanBulkUpload.vue'
import CustomerDetails from '../../CollectionModules/CustomerDetails.vue'
import { chipStatusColors } from '@/utils/workforce/statusColors'
import {
  convertToTitleCase,
  fullName,
  debounce,
  dayHourFormat,
  isModuleFeatureAllowed,
  hasPermission,
  mergeOptions,
  mergeOptionsDetails,
  calculateOverdueDays,
  isEmailTimelineEnabled
} from '@/utils/common'
import FilterPopOver from '@/components/FilterPopOver.vue'
import ExportDynamicColumns from '../ExportDynamicColumns.vue'
import UpdateCases from './UpdateCases.vue'
import { setDataOnEnter } from '@/utils/workforce/utils'
import permissionData from '@/utils/permissions'
import _ from 'lodash'
import FloatingActionBar from './FloatingActionBar.vue'
import { axiosUrl } from '@/utils/workforceApi'
import { UpdateCaseDialogLabels } from '@/utils/workforce/constants'
import Sortable from 'sortablejs'
import dayjs from 'dayjs'
import EmailTimelineTable from './EmailTimelineTable.vue'

export default {
  name: 'CaseTable',
  props: {
    caseConfig: Object
    // entities: Array,
  },
  components: {
    EcomFilterCategory,
    ActionButton,
    AddEditCase,
    UploadCases,
    AddEditCaseTask,
    CaseDetails,
    AlertPopUp,
    UserAvatar,
    OrderStatuses,
    FloatingActionBar,
    // FilterPopOver,
    UpdateCases,
    ExportDynamicColumns,
    AddEditLoanManagement,
    LoanBulkUpload,
    CustomerDetails,
    EmailTimelineTable
  },
  inject: {
    getOrgConfig: {
      from: 'getOrgConfig',
      default: () => () => null
    }
  },
  data() {
    return {
      UpdateCaseDialogLabels,
      userOrg: this.$storage.getUniversal('user'),
      userData: this.$store.state.wfmPermissions,
      headers: [],
      subCases: this.$store.state.subCases,
      openEditCase: false,
      openUploadCases: false,
      menu: false,
      showConfirmationDialog: false,
      downloadMenu: false,
      openAddCaseTask: false,
      openCaseDetails: false,
      showUpdateVisitDialog: false,
      selectedCase: null,
      filterCaseOutcome: '',
      filterStatus: '',
      filterLoanStatus: '',
      loanStatusOptions: [
        { label: 'Applied', value: 'Applied' },
        { label: 'Under Review', value: 'Under Review' },
        { label: 'Sanctioned', value: 'Sanctioned' },
        { label: 'Disbursal Initiated', value: 'Disbursal Initiated' },
        { label: 'Disbursed', value: 'Disbursed' },
        { label: 'Active', value: 'Active' },
        { label: 'Closed', value: 'Closed' },
        { label: 'Defaulted', value: 'Defaulted' },
        { label: 'NPA', value: 'NPA' },
        { label: 'Written Off', value: 'Written Off' },
        { label: 'Foreclosed', value: 'Foreclosed' },
        { label: 'Deleted', value: 'Deleted' },
        { label: 'Open', value: 'Open' },
        { label: 'Completed', value: 'Completed' },
        { label: 'Pending Approvals', value: 'PendingApprovals' },
        { label: 'Rejected', value: 'Rejected' },
        { label: 'Settled', value: 'Settled' },
        { label: 'Canceled', value: 'Canceled' }
      ],
      cases: [],
      employees: [],
      editCase: null,
      selectedCaseId: null,
      filterAssignedTo: '',
      filterCase: '',
      loadTable: true,
      exportLoading: false,
      options: { sortBy: [], sortDesc: [], itemsPerPage: 10, page: 1 },
      totalItems: 0,
      filterCustomer: '',
      filterBranch: '',
      filterTeam: '',
      newAssignedTo: null,
      visitAssignedTo: null,
      visitId: null,
      dates: [],
      branches: [],
      teams: [],
      entities: [],
      startDate: null,
      endDate: null,
      awsBaseUrl: process.env.VUE_APP_S3_BASE_URL,
      caseStatusOptions: [
        { label: 'Open', value: 'OPEN' },
        { label: 'Closed', value: 'CLOSED' },
        { label: 'In-Progress', value: 'IN_PROGRESS' }
      ],
      selectedCount: 0,
      selected: [],
      showDialog: false,
      showDeleteDialog: false,
      departments: [],
      designations: [],
      dialogLable: '',
      deleteCustomer: false,
      loading: false,
      caseAction: '',
      showDeleteAllDialog: false,
      selectValue: 'CASE_NO',
      exportDialog: false,
      exportColumnHeaders: [],
      dynamicColumns: [],
      entityId: null,
      searchValue: '',
      // Partner filter properties
      filterPartner: null,
      // Loan bulk upload properties
      openLoanBulkUploadDialog: false,
      arrangeMode: false,
      sortable: null,
      openCustomerDetails: false,
      currentCustomer: null,
      statusTitle: 'Status',
      outcomeTitle: 'Outcome',
      filterTag: 'null',
      activeEditingCaseId: null,
      customersSearchList: [],
      loadingCustomers: false,
      customerSearchInput: '',
      showCustomerMenu: false
    }
  },

  beforeCreate() {
    this.debouncedFetchCases = () => {}
  },
  created() {
    this.debouncedFetchCases = debounce(this.fetchCases, 500)
  },
  watch: {
    arrangeMode(val) {
      if (val) {
        this.$nextTick(() => {
          this.initSortable()
        })
      } else {
        if (this.sortable) {
          this.sortable.destroy()
          this.sortable = null
        }
      }
    },
    '$route.query.search': {
      handler(newQuery) {
        try {
          const newVal = newQuery || ''
          const oldVal = this.filterCustomer || ''
          this.filterCustomer = newQuery || ''
          if (newVal !== oldVal) {
            this.options.page = 1
            this.debouncedFetchCases()
          }
        } catch (error) {
          console.log(error)
        }
      },
      immediate: true
    },

    '$route.query.empId': {
      handler(empId) {
        try {
          const newVal = empId || ''
          const oldVal = this.filterAssignedTo || ''
          this.filterAssignedTo = empId || ''
          if (newVal !== oldVal) {
            this.options.page = 1
            this.debouncedFetchCases()
          }
        } catch (error) {
          console.log(error)
        }
      },
      immediate: true
    },

    '$route.query.partnerId': {
      handler(partnerId) {
        try {
          const newVal = partnerId || ''
          const oldVal = this.filterPartner || ''
          this.filterPartner = partnerId || ''
          if (newVal !== oldVal) {
            this.handleChange()
          }
        } catch (error) {
          console.log(error)
        }
      },
      immediate: true
    },
    '$route.query.caseNo': {
      handler(caseNo) {
        try {
          const newVal = caseNo || ''
          const oldVal = this.filterCase || ''
          this.filterCase = caseNo || ''
          if (newVal !== oldVal) {
            this.options.page = 1
            this.debouncedFetchCases()
          }
        } catch (error) {
          console.log(error)
        }
      },
      immediate: true
    },
    filterCase(val) {
      if (!val && this.$route.query.search) {
        this.$router.replace({ query: {} }).catch((err) => {
          if (err.name !== 'NavigationDuplicated') {
            throw err
          }
        })
      }
    },
    caseConfig: {
      async handler(config) {
        const caseQuery = this.$route.query?.search
        await this.fetchUsers()

        if (caseQuery) {
          this.filterCase = caseQuery
        }
        if (config) {
          this.debouncedFetchCases()
          this.caseStatusOptions = config.caseStatuses?.length
            ? config.caseStatuses
            : [
                { label: 'Open', value: 'OPEN' },
                { label: 'Closed', value: 'CLOSED' },
                { label: 'In-Progress', value: 'IN_PROGRESS' }
              ]
          const fm = { ...config?.baseFieldMappings, ...config?.fieldMappings }
          // console.log({ fm })
          const mergedOptionsDetails = mergeOptionsDetails(config.userDefinedCaseStatus)
          const isMergedOptions = mergedOptionsDetails.length > 0
          const getText = (displayName, key) => displayName || key
          const branch = {
            text: 'Branch',
            value: 'branchName',
            sortable: false
          }

          const action = {
            text: 'Action',
            value: 'action',
            sortable: false,
            width: 'auto'
          }
          const loanamount = {
            text: 'Loan Amount',
            value: 'loanamount',
            sortable: false
          }

          const customerName = {
            text: 'Customer',
            value: 'customer',
            sortable: false
          }
          const partnerName = {
            text: 'Lender',
            value: 'lender',
            sortable: false
          }
          const outcome = {
            key: 'outcome',
            value: 'outcome',
            text: getText(
              fm.outcome?.displayName || fm.caseOutcomeStatus?.displayName,
              'Follow-Up Outcome'
            )
          }
          const disbursements = {
            text: 'Disbursements',
            value: 'disbursements',
            sortable: false
          }
          const totalPendingEMI = {
            text: 'EMI(s)',
            value: 'emi(s)',
            sortable: false
          }
          const principalAmount = {
            text: 'Principal',
            value: 'principal',
            sortable: false
          }
          const interest = {
            text: 'Interest',
            value: 'interest',
            sortable: false
          }
          const lateFee = {
            text: 'Late Fee',
            value: 'latefee',
            sortable: false
          }
          const loanType = {
            key: 'typeOfLoan',
            text: getText(fm.typeOfLoan?.displayName, 'Loan Type')
          }
          const loanCategoryCol = {
            key: 'loanCategory',
            text: getText(fm.loanCategory?.displayName, 'Category')
          }
          const leadSourceCol = {
            key: 'leadSource',
            text: getText(fm.leadSource?.displayName, 'Lead Source')
          }
          const status = {
            key: 'status',
            value: 'status',
            text: getText(fm.status?.displayName || fm.currentStatus?.displayName, 'Status')
          }
          this.statusTitle = getText(
            fm.status?.displayName || fm.currentStatus?.displayName,
            'Status'
          )
          this.outcomeTitle = getText(
            fm.outcome?.displayName || fm.caseOutcomeStatus?.displayName,
            'Outcome'
          )
          const extraCustomFields = []
          if (config.additionalColumns) {
            Object.entries(config.additionalColumns).forEach(([key, col]) => {
              if (col.tableVisibility !== false) {
                extraCustomFields.push({
                  key: key,
                  value: `additionalColumns.${key}`,
                  text: col.displayName,
                  sortable: false
                })
              }
            })
          }

          const headers = [
            {
              key: 'caseNo',
              text: getText(fm.caseNo?.displayName, 'caseNo'),
              width: '200px'
            },
            ...(this.isLoanManagement || !fm.typeOfLoan ? [] : [loanType]),
            // ...(this.isLoanManagement ? [partnerName] : []),
            ...(this.isLoanManagement ? [customerName] : [customerName]),
            ...(this.isLoanManagement ? [loanType, loanCategoryCol, leadSourceCol] : []),
            ...(this.isLoanManagement ? [loanamount] : []),
            ...(this.isLoanManagement ? [disbursements] : []),
            ...(this.isLoanManagement ? [principalAmount] : []),
            ...(this.isLoanManagement ? [interest] : []),
            ...(this.isLoanManagement ? [totalPendingEMI] : []),
            ...(this.isLoanManagement ? [lateFee] : []),
            ...(isMergedOptions ? [outcome] : []),
            ...(config.branch ? [branch] : [branch]),
            ...(this.isLoanManagement ? [] : [status]),
            ...extraCustomFields,
            {
              key: 'assignedTo',
              text: getText(fm.assignedTo?.displayName, 'assignedTo')
            },
            ...(this.canEdit || this.canDelete || this.canWrite ? [action] : [])
          ]

          if (!this.isLoanManagement) {
            const extraCols = []
            if (fm.bucket) {
              extraCols.push({
                key: 'bucket',
                value: 'bkt',
                text: getText(fm.bucket?.displayName, 'bucket')
              })
            }
            if (fm.principalOutstanding) {
              extraCols.push({
                key: 'principalOutstanding',
                value: 'pos',
                text: getText(fm.principalOutstanding?.displayName, 'principalOutstanding')
              })
            }
            if (fm.emiAmount) {
              extraCols.push({
                key: 'emiAmount',
                text: getText(fm.emiAmount?.displayName, 'emiAmount')
              })
            }
            if (fm.tenure) {
              extraCols.push({
                key: 'tenure',
                text: getText(fm.tenure?.displayName, 'tenure')
              })
            }
            if (extraCols.length) {
              headers.splice(2, 0, ...extraCols)
            }
          }

          const caseOutcomeIndex = headers.findIndex((header) => header.key === 'outcome')

          if (isMergedOptions) {
            const filteredOptions = mergedOptionsDetails.filter((el) =>
              el.fields?.some((f) => f.name === 'amount' && f.visibility === true)
            )

            const Columns = filteredOptions.map((option) => ({
              text: `${option.code} Amt.`,
              value: `${option.code}`,
              key: `${option.code}`,
              sortable: false
            }))

            this.dynamicColumns = Columns
            if (caseOutcomeIndex !== -1) {
              headers.splice(caseOutcomeIndex + 1, 0, ...Columns)
            }
          }
          // console.log({ headers, fm })
          const x = headers
            .filter((col) => {
              if (config.additionalColumns && config.additionalColumns[col.key]) {
                return true
              }
              return !col.key || fm[col.key]?.tableVisibility !== false
            })
            // .filter((col) => !col.key || fm[col.key]?.visible !== false)
            .map((col) => ({
              text: col.text,
              value: col.value || col.key || col.text.toLowerCase().replace(/ /g, ''),
              sortable: false,
              width: col.width || undefined,
              headerClass: 'font-medium whitespace-nowrap overflow-hidden text-ellipsis'
            }))

          // Adding a fixed column
          // if (this.caseConfig?.branch) {
          //   x.splice(2, 0, {
          //     text: 'Distance From Branch',
          //     value: 'distanceFromBranch',
          //     sortable: false,
          //     width: '150px'
          //   })
          // }
          if (this.isEntityEnabled) {
            x.splice(2, 0, {
              text: config?.entity?.displayName,
              value: 'entityName',
              sortable: false
            })
          }
          if (this.isTeamLinked) {
            x.splice(2, 0, {
              text: 'Team',
              value: 'teamName',
              sortable: false
            })
          }
          this.headers = x
        }
      },
      immediate: true
    },
    options: {
      handler(newOptions, oldOptions) {
        if (
          newOptions.itemsPerPage !== oldOptions.itemsPerPage ||
          newOptions.page !== oldOptions.page
        ) {
          this.debouncedFetchCases()
        }
      },
      deep: true
    },
    formattedDate() {
      this.updateSelectedCount()
    }
  },
  computed: {
    isGroupedData() {
      return this.cases && this.cases.some((c) => c.isGroup)
    },
    orgConfig() {
      return this.getOrgConfig()
    },
    isEmailTimelineEnabled() {
      return isEmailTimelineEnabled(this.orgConfig, this.caseConfig?._id)
    },
    isFilterApplied() {
      return !!this.filterAssignedTo
    },
    computedHeaders() {
      const headers = this.headers.map((h) => ({
        ...h,
        sortable: this.arrangeMode ? false : h.sortable
      }))
      if (this.arrangeMode) {
        headers.unshift({
          text: '',
          value: 'drag',
          sortable: false,
          width: '30px',
          align: 'center'
        })
      }
      return headers
    },
    canEdit() {
      return hasPermission(this.userData, permissionData.collectionEdit)
    },
    canDelete() {
      return hasPermission(this.userData, permissionData.collectionDelete)
    },
    canWrite() {
      return hasPermission(this.userData, permissionData.collectionWrite)
    },
    isLoanManagement() {
      return this.$route?.params?.useCase === 'loan_management'
    },

    selectItems() {
      const caseLang = this.caseConfig?.fieldMappings
      if (caseLang?.bkt?.displayName) {
        return [
          { text: caseLang?.caseNo?.displayName, value: 'CASE_NO' },
          { text: caseLang?.bkt?.displayName || 'Bucket', value: 'BKT' }
        ]
      }
      return [{ text: caseLang?.caseNo?.displayName, value: 'CASE_NO' }]
    },
    searchPlaceholder() {
      const caseLang = this.caseConfig?.fieldMappings
      if (this.selectValue === 'BKT') {
        return `Search by ${caseLang?.bkt?.displayName}`
      }
      return `Search by ${caseLang?.caseNo?.displayName}`
    },
    caseAssignedLang() {
      // const caseLang = this.caseConfig?.fieldMappings;
      return 'caseLang.assignedTo?.displayName'
    },
    tabText() {
      return this.caseConfig?.tableTabMapping || {}
    },
    users() {
      return [
        ...this.employees.map((emp) => ({
          ...emp,
          fullName: `${fullName(emp)} ${emp.wfmRole?.role ? ` - ${emp.wfmRole.role}` : ''}`
        }))
        // {
        //   _id: 'null',
        //   fullName: 'Unassigned'
        // }
      ]
    },
    userDefinedStatusOptions() {
      const statusArr = this.caseConfig?.userDefinedCaseStatus || []
      const items = []
      statusArr.forEach((category, index) => {
        if (category.categoryName) {
          items.push({ header: category.categoryName })
        }
        const options = category.optionsList || category.options || []
        if (Array.isArray(options)) {
          options.forEach((opt) => {
            if (typeof opt === 'string') {
              items.push({ text: opt, value: opt })
            } else if (typeof opt === 'object' && opt !== null) {
              items.push({
                ...opt,
                text: opt.text || opt.code || opt.name || 'Unknown',
                value: opt.value || opt.code || opt._id
              })
            }
          })
        }
        if (index < statusArr.length - 1) {
          items.push({ divider: true })
        }
      })
      return items
    },
    userDefinedOutcomeOptions() {
      const statusArr = this.caseConfig?.userDefinedCaseStatus || []
      const items = []
      statusArr.forEach((category, index) => {
        if (category.categoryName) {
          items.push({ header: category.categoryName, isHeader: true })
        }
        const options = category.optionsList || category.options || []
        if (Array.isArray(options)) {
          options.forEach((opt) => {
            if (typeof opt === 'string') {
              items.push({ text: opt, value: opt, isHeader: false })
            } else if (typeof opt === 'object' && opt !== null) {
              items.push({
                ...opt,
                text: opt.text || opt.code || opt.name || 'Unknown',
                value: opt.value || opt.code || opt._id,
                isHeader: false
              })
            }
          })
        }
        if (index < statusArr.length - 1) {
          items.push({ divider: true, isDivider: true })
        }
      })
      if (items.length > 0) {
        items.push({ divider: true, isDivider: true })
      }
      items.push({ text: 'Not-Set', value: 'null' })
      return items
    },
    formattedDate() {
      if (this.dates.length === 2) {
        const startDate = this.visibleDate(this.dates[0])
        const endDate = this.visibleDate(this.dates[1])
        return `${startDate} to ${endDate}`
      } else if (this.dates.length === 1) {
        return this.visibleDate(this.dates[0])
      }
      return ''
    },
    isEntityEnabled() {
      return this.caseConfig.entity?.enabled || false
    },
    isTeamLinked() {
      const selectedTeam = this.teams?.find((team) => team?.linkedLicense === this.caseConfig?._id)
      return selectedTeam?._id ? true : false
    },
    emailCategories() {
      const list = this.caseConfig?.emailCategories || []
      if (!list.length) return []

      const items = [
        { text: 'All', value: 'all' },
        { text: 'Without Tag', value: 'null' }
      ]

      list.forEach((cat) => {
        if (cat.name) {
          items.push({ text: cat.name, value: cat.name })
        }
      })
      return items
    },
    entityTitle() {
      return this.caseConfig.entity?.displayName || 'Entity'
    },
    routeApiCollection() {
      return !this.isLoanManagement
    },
    isExportDisabled() {
      // Disable export if there's no data
      return this.totalItems === 0
    },
    computedTeams() {
      if (!this.teams || this.teams.length === 0) return []
      if (!this.filterBranch) return this.teams

      const enabledTeams = []
      const disabledTeams = []

      this.teams.forEach((team) => {
        if (team.branch === this.filterBranch) {
          enabledTeams.push(team)
        } else {
          disabledTeams.push(team)
        }
      })

      return [...enabledTeams, ...disabledTeams]
    }
  },
  methods: {
    clearAllFilters() {
      this.filterAssignedTo = null
      this.filterCaseOutcome = null
      this.filterBranch = null
      this.filterTeam = null
      this.entityId = null
      this.filterStatus = null
      this.filterLoanStatus = null
      this.filterTag = 'null'
      this.dates = []
      this.formattedDate = null
      this.startDate = null
      this.endDate = null
      if (this.handleChange) this.handleChange()
    },
    setDataOnEnter,
    // truncateHeader(text) {
    //   const maxLength = 15
    //   if (text.length > maxLength) {
    //     return text.slice(0, maxLength) + '...'
    //   }
    //   return text
    // },
    async getDistanceFromBranch(item) {
      const params = {
        lat: item.lat,
        long: item.long
      }
      const distanceFromBranch = await this.getDistanceFromBranch(item)
      this.caseStatusUpdate(distanceFromBranch, item, 'distanceFromBranch')

      return this.$axios.get('/distance-from-branch', params)
    },
    loanCompletedStatus(loanWorkflow) {
      if (!loanWorkflow) return ''
      let completed = 0
      const totalSteps = loanWorkflow?.steps?.length || 0
      loanWorkflow?.steps?.forEach((step) => {
        if (step.status === 'COMPLETED') {
          completed++
        }
      })
      return Math.floor((completed / totalSteps) * 100) + '%'
    },
    getLoanCompletionColor(loanWorkflow) {
      if (!loanWorkflow || !loanWorkflow.steps?.length) {
        return this.statusColor('not_completed')?.textColor || '#6B7280'
      }

      let completed = 0
      const totalSteps = loanWorkflow.steps.length
      loanWorkflow.steps.forEach((step) => {
        if (step.status === 'COMPLETED') {
          completed++
        }
      })

      const percentage = Math.floor((completed / totalSteps) * 100)

      if (percentage === 100) {
        return this.statusColor('completed')?.textColor || '#16A34A'
      } else if (percentage >= 75) {
        return this.statusColor('approved')?.textColor || '#059669'
      } else if (percentage >= 50) {
        return this.statusColor('in_progress')?.textColor || '#3399FF'
      } else if (percentage >= 25) {
        return this.statusColor('pending')?.textColor || '#F59E0B'
      } else if (percentage > 0) {
        return this.statusColor('late_paid')?.textColor || '#F97316'
      } else {
        return this.statusColor('not_completed')?.textColor || '#DC2626'
      }
    },
    extractValue(item) {
      if (!item) return '-'

      try {
        if (typeof item === 'string' && /^{.*}$/.test(item)) {
          const parsed = JSON.parse(item)
          return parsed.text || parsed.value || item
        }
        return typeof item === 'object' ? item.text || item.value || JSON.stringify(item) : item
      } catch {
        return item
      }
    },
    statusColor(colors) {
      return chipStatusColors[colors?.toLowerCase()] || { textColor: 'grey' }
    },
    getStatusInfo(status) {
      const statusMap = {
        Applied: { color: 'blue', icon: 'mdi-file-plus' },
        'Under Review': { color: 'amber', icon: 'mdi-magnify' },
        Sanctioned: { color: 'indigo', icon: 'mdi-check-circle' },
        'Disbursal Initiated': { color: 'cyan', icon: 'mdi-bank-transfer-out' },
        Disbursed: { color: 'teal', icon: 'mdi-bank-transfer' },
        Active: { color: 'green', icon: 'mdi-check-decagram' },
        Closed: { color: 'grey', icon: 'mdi-lock' },
        Defaulted: { color: 'red', icon: 'mdi-alert' },
        NPA: { color: 'deep-orange', icon: 'mdi-alert-octagon' },
        'Written Off': { color: 'brown', icon: 'mdi-delete-sweep' },
        Foreclosed: { color: 'red darken-4', icon: 'mdi-bank-off' },
        Deleted: { color: 'red darken-2', icon: 'mdi-delete-forever' },
        Open: { color: 'success', icon: 'mdi-folder-open' },
        Completed: { color: 'primary', icon: 'mdi-check-all' },
        PendingApprovals: { color: 'orange', icon: 'mdi-clock-alert' },
        Rejected: { color: 'error', icon: 'mdi-close-circle' },
        Settled: { color: 'deep-purple', icon: 'mdi-currency-usd' },
        Canceled: { color: 'grey darken-3', icon: 'mdi-cancel' }
      }
      return statusMap[status] || { color: 'blue-grey darken-3', icon: 'mdi-help-circle' }
    },
    getBranchDistance(item) {
      const distance = _.get(item, 'distanceFromBranch.[0].distance')
      return distance ? `${distance} km` : '-'
    },
    isBranchFeatureVisible(feature) {
      return isModuleFeatureAllowed('BRANCH_MANAGEMENT', feature)
    },
    filterParams() {
      const params = {}
      if (this.startDate && this.endDate) {
        params.start = this.startDate
        params.end = this.endDate
      }
      if (this.caseConfig?.name && !this.isLoanManagement) {
        params.subCase = this.caseConfig._id
        // params.caseType = this.caseConfig?.name
      }
      if (this.filterAssignedTo) {
        params.assignedTo = this.filterAssignedTo
      }

      // Only include userDefinedCaseStatus if it has a value
      if (this.filterCaseOutcome) {
        params.userDefinedCaseStatus = this.filterCaseOutcome
      }
      if (this.isEmailTimelineEnabled) {
        params.groupBy = 'customer'
      }
      if (this.filterStatus) {
        params.status = this.filterStatus
      }

      if (this.filterLoanStatus) {
        params.loanStatus = this.filterLoanStatus
      }

      if (this.filterCase) {
        if (this.selectValue === 'BKT') {
          params.bktSearch = this.filterCase
        } else {
          params.search = this.filterCase
        }
      }
      if (this.filterCustomer) {
        params.customerSearch = this.filterCustomer
      }
      if (this.entityId) {
        params.entity = this.entityId
      }
      if (this.filterPartner) {
        params.partner = this.filterPartner
      }
      if (this.filterTag) {
        const hasEmailCategories =
          this.caseConfig?.emailCategories && this.caseConfig.emailCategories.length > 0
        if (hasEmailCategories) {
          params.tag = this.filterTag
        }
      }
      return params
    },
    isAssigned(assignedTo, userId) {
      if (!assignedTo || !Array.isArray(assignedTo)) return false
      return assignedTo.some((u) => (u._id || u) === userId)
    },
    handleAssignedToChange(val, item) {
      let next = val || []
      if (next.includes('null')) {
        next = []
      } else {
        next = next.filter((id) => id && id !== 'null')
      }
      this.$set(item, 'assignedTo', next)
      this.caseStatusUpdate(next, item, 'assignedTo')
    },
    getAllEntities: debounce(async function (text) {
      try {
        // if (!text || this.entityId) return;
        const params = {}
        if (this.searchValue) {
          params.search = this.searchValue
        }
        const response = await this.$axios.get(axiosUrl.cases.getEntities, { params })
        this.entities = response?.data?.entities
      } catch (error) {
        console.log(error)
      }
    }, 500),
    async fetchCases() {
      try {
        if (!this.caseConfig?.name) return
        const { sortBy, sortDesc, page, itemsPerPage } = this.options
        const filterParams = this.filterParams()
        const params = {
          page,
          limit: itemsPerPage > -1 ? itemsPerPage : this.totalItems,
          ...filterParams
        }
        if (sortBy.length > 0) {
          params.sortBy = sortBy[0]
        }
        if (sortDesc.length > 0) {
          params.sortOrder = sortDesc[0] ? 'desc' : 'asc'
        }

        if (this.filterBranch) {
          params.branch = this.filterBranch
        }
        if (this.filterTeam) {
          params.team = this.filterTeam
        }
        this.loadTable = true
        let url
        let key = 'collections'
        if (this.routeApiCollection) {
          url = this.isEmailTimelineEnabled ? '/collection/groups' : `${axiosUrl.cases.getCases}`
          key = this.isEmailTimelineEnabled ? 'groups' : 'collections'
        } else {
          url = `${axiosUrl.cases.getLoanCases}=${this.caseConfig._id}`
          key = 'loans'
        }
        const response = await this.$axios.get(url, {
          params
        })
        // console.log(params, response)
        const rawCases = response.data?.[key] || []
        this.cases = rawCases.map((item) => ({
          ...item,
          userDefinedCaseStatus:
            typeof item.userDefinedCaseStatus === 'object'
              ? item.userDefinedCaseStatus?.value || item.userDefinedCaseStatus?.text
              : item.userDefinedCaseStatus
        }))
        // this.cases = rawCases.filter((item) => {
        //   const status = (item.loanDetails?.currentStatus || item.status || '').toUpperCase()
        //   return !['DELETED', 'REJECTED'].includes(status)
        // })
        this.loadTable = false
        this.totalItems = response.data?.pagination?.totalCount

        if (this.$route.query.caseNo && this.cases?.length === 1) {
          this.caseDetails(this.cases[0])
        }
      } catch (error) {
        this.loadTable = false
        console.log(error)
        const errorMessage = error?.response?.data?.message || error
        this.$toast.error(errorMessage)
      }
    },
    async caseStatusUpdate(newValue, item, field) {
      try {
        let payload
        if (field === 'assignedTo') {
          payload = {
            [field]: newValue.map((el) => el._id || el),
            acknowledged: ''
          }
        } else {
          payload = {
            [field]: newValue
          }
        }
        const id = item._id
        const config = {}
        if (field === 'customer') {
          config.params = { updateEmail: true }
        }
        const response = await this.$axios.put(
          `${axiosUrl.cases.updateCase}/${id}`,
          payload,
          config
        )
        if (field === 'assignedTo') {
          this.visitAssignedTo = newValue
          // const newUser = this.users.find(
          //   (user) => user._id === item.assignedTo
          // );
          // this.newAssignedTo = newUser?.fullName;
          // this.visitId = item.tasks[0]?._id;
          // this.showUpdateVisitDialog = true;
        }
        const successMessage = response?.data?.message || 'Case updated successfully'
        this.$toast.success(successMessage)
        this.cancel()
      } catch (error) {
        this.$toast.error(error.response?.data?.message)
        console.error(error)
      }
    },
    async updateVisit() {
      const payload = {
        assignedTo: this.visitAssignedTo
      }
      await this.$axios.put(`${axiosUrl.cases.updateVisit}/${this.visitId}`, payload)
      this.$toast.success(`Pending visits assigned to ${this.newAssignedTo} successfully.`)
      this.cancelVisitUpdate()
    },
    debounceSearchCustomer: debounce(async function (val) {
      if (!val || typeof val !== 'string' || !val.trim()) {
        this.customersSearchList = []
        return
      }
      await this.searchCustomerForCase(val)
    }, 500),
    startEditingCustomer(item) {
      this.activeEditingCaseId = item._id
      const initialName = item.customer?.customerFullName || item.customer?.name || ''
      this.customerSearchInput = initialName
      this.customersSearchList = []
      this.showCustomerMenu = true
      if (initialName) {
        this.searchCustomerForCase(initialName)
      }
    },
    async searchCustomerForCase(val) {
      if (!val || typeof val !== 'string' || !val.trim()) return
      this.loadingCustomers = true
      this.showCustomerMenu = true
      try {
        const linkedSubCase = (this.subCases || []).find(
          (s) => s._id === this.caseConfig?.crossLinkedSubCaseId
        )
        const response = await this.$axios.get('/customers/search-linked', {
          params: {
            search: val,
            useCase: linkedSubCase?.useCase || 'loan_management'
          }
        })
        const rawList = response.data.data || response.data.customers || []

        const flattenedOptions = []
        rawList.forEach((cust) => {
          const custName = cust.name || cust.customerFullName || 'Customer'
          if (cust.cases && cust.cases.length > 0) {
            cust.cases.forEach((c) => {
              flattenedOptions.push({
                ...cust,
                optionId: `${cust._id}_${c._id}`,
                customerId: cust._id,
                caseId: c._id,
                caseNo: c.caseNo,
                customerName: custName,
                displayName: `${custName} (Case: ${c.caseNo || c._id})`,
                rawCustomer: cust
              })
            })
          } else {
            flattenedOptions.push({
              ...cust,
              optionId: `${cust._id}_no_case`,
              customerId: cust._id,
              caseId: null,
              caseNo: null,
              customerName: custName,
              displayName: custName,
              rawCustomer: cust
            })
          }
        })
        this.customersSearchList = flattenedOptions
      } catch (error) {
        console.error('Error searching customers:', error)
      } finally {
        this.loadingCustomers = false
      }
    },
    async updateCaseCustomer(item, selectedOption) {
      if (!selectedOption || !selectedOption.customerId) return
      try {
        const payload = {
          customer: selectedOption.customerId
        }
        if (selectedOption.caseId) {
          payload.cross_case_linking = [selectedOption.caseId]
        }

        const config = {
          params: { updateEmail: true }
        }
        const response = await this.$axios.put(
          `${axiosUrl.cases.updateCase}/${item._id}`,
          payload,
          config
        )
        this.$toast.success(response?.data?.message || 'Customer updated successfully')
        this.activeEditingCaseId = null
        this.showCustomerMenu = false
        this.fetchCases()
      } catch (error) {
        console.error('Error linking customer/case:', error)
        this.$toast.error(error.response?.data?.message || 'Error updating customer')
      }
    },
    handleDelete(item) {
      this.showConfirmationDialog = true
      this.selectedCaseId = item._id
    },
    async caseDelete() {
      try {
        const params = {}
        if (this.caseConfig.name) {
          params.caseType = this.caseConfig.name
        }
        const response = await this.$axios.delete(
          `${axiosUrl.cases.deleteCase}/${this.selectedCaseId}`,

          params
        )
        const index = this.cases.findIndex((caseEl) => caseEl._id === this.selectedCaseId._id)
        if (index !== -1) {
          this.cases.splice(index, 1)
        }
        this.selectedCaseId = null
        const successMessage = response?.data?.message || 'Case deleted successfully.'
        this.$toast.success(successMessage)
        this.cancel()
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message || 'An error occurred while deleting the case.'
        this.$toast.error(errorMessage)
        console.error(error)
      }
    },
    async deleteAllCases() {
      this.loading = true
      try {
        const caseType = this.caseConfig?.name
        const response = await this.$axios.delete(`${axiosUrl.cases.deletAllCase}=${caseType}`)
        this.loading = false
        if (response?.data?.success) {
          this.$toast.success(response?.data?.message)
          this.handleShowDialogDeleteAll(false)
        }
      } catch (error) {
        this.loading = false
        const errorMessage =
          error?.response?.data?.message || 'An error occurred while deleting the case.'
        this.$toast.error(errorMessage)
        console.error(error)
      }
    },

    toggleArrangeMode() {
      this.arrangeMode = !this.arrangeMode
      if (this.arrangeMode) {
        this.options.sortBy = []
        this.options.sortDesc = []
      }
    },
    initSortable() {
      const tbody = this.$el.querySelector('.v-data-table__wrapper tbody')
      if (tbody && !this.sortable) {
        this.sortable = Sortable.create(tbody, {
          handle: '.drag-handle',
          animation: 150,
          onEnd: async ({ oldIndex, newIndex }) => {
            if (oldIndex === newIndex) return
            const movedItem = this.cases.splice(oldIndex, 1)[0]
            this.cases.splice(newIndex, 0, movedItem)
            await this.saveCaseOrder()
          }
        })
      }
    },
    async saveCaseOrder() {
      const userId = this.filterAssignedTo
      if (!userId) {
        this.$toast.error('Please select an assignee to save the case order.')
        return
      }

      const caseOrder = this.cases.map((c) => c._id)
      const payload = {
        userId,
        caseOrder
      }

      try {
        let orderExists = false
        try {
          const { data } = await this.$axios.get('/collection/case-order', {
            params: { userId }
          })
          if (data && data.success && data.data) {
            orderExists = true
          }
        } catch (e) {
          // If 404 or other error, assume no order exists and try POST
          orderExists = false
        }

        if (orderExists) {
          await this.$axios.put(`/collection/case-order/${userId}`, payload)
        } else {
          await this.$axios.post('/collection/case-order', payload)
        }
        this.$toast.success('Case order updated successfully')
      } catch (error) {
        console.error('Error saving case order:', error)
        this.$toast.error('Failed to save case order')
      }
    },

    async singleCaseDelete(item, params) {
      try {
        let customerId = item?.customer?._id
        let selectedCaseId = item._id
        let url
        if (this.deleteCustomer) {
          url = `${axiosUrl.cases.deleteCase}/${selectedCaseId}?customerId=${customerId}`
        } else {
          url = `${axiosUrl.cases.deleteCase}/${selectedCaseId}`
        }
        let response = await this.$axios.delete(url, params)
        return response.data
      } catch (error) {
        console.error(error)
      }
    },
    async casesDeleteFunc() {
      this.loading = true
      const params = {}
      if (this.caseConfig.name) {
        params.caseType = this.caseConfig.name
      }
      const promises = []

      // Make API calls and store promises in the array
      this.selected?.forEach(async (item) => {
        promises.push(this.singleCaseDelete(item, params))
      })

      // Wait for all API calls to complete
      try {
        const results = await Promise.all(promises)

        if (!results.includes(undefined)) {
          this.$toast.success('Cases deleted successfully.')
        }
        this.loading = false
        this.deleteCustomer = false
        this.selected = []
        this.handleShowDialogDelete(false)
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message || 'An error occurred while deleting the case.'
        this.$toast.error(errorMessage)
        console.error(error)
      }
    },

    handleCheckedUpdate(value) {
      this.deleteCustomer = value
    },

    async fetchUsers() {
      try {
        const params = {
          limit: '*',
          sortBy: 'firstName',
          sortOrder: 'asc'
        }
        if (this.userOrg?.branch && !this.userOrg?.role?.includes('WFM_ADMIN')) {
          params.branch = this.userOrg?.branch
        }
        if (this.caseConfig.branch) {
          params.branch = this.caseConfig?.branch
        }
        const response = await this.$axios.get(axiosUrl.users.getUsers, {
          params
        })
        this.employees = response.data?.users
      } catch (error) {
        console.log(error)
      }
    },
    onDateSelected() {
      if (this.dates?.length === 2) {
        this.startDate = this.formatDate(this.dates[0])
        this.endDate = this.formatDate(this.dates[1])
        this.options.page = 1
        this.fetchCases()
      } else {
        this.startDate = this.formatDate(this.dates[0])
        this.endDate = null
      }
    },
    async onDateCleared() {
      this.dates = []
      this.startDate = null
      this.endDate = null
      await this.fetchCases()
    },
    exportXlsxFormat() {
      const XLSX = require('xlsx')
      const restructuredData = this.prepareData(this.subCases)
      const caseType = this.caseConfig?.name
      const workbook = XLSX.utils.book_new()
      const worksheet = XLSX.utils.aoa_to_sheet(restructuredData)
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1')
      XLSX.writeFile(workbook, `${caseType}_Case.xlsx`)
    },
    prepareData(data) {
      const tableData = []

      data.forEach(() => {
        const row = []
        tableData.push(row)
      })

      const headerRow = []
      const uniqueHeaders = new Set()
      const subCase = this.caseConfig

      if (subCase.fieldMappings) {
        Object.values(subCase?.fieldMappings).forEach((field) => {
          // field?.visibility &&
          if (field && field?.displayName && !uniqueHeaders.has(field?.displayName)) {
            headerRow.push(field?.displayName)
            uniqueHeaders.add(field?.displayName)
          }
        })
      }

      if (subCase?.additionalColumns) {
        Object.values(subCase?.additionalColumns)?.forEach((add) => {
          if (add?.displayName && !uniqueHeaders.has(add.displayName)) {
            headerRow.push(add.displayName)
            uniqueHeaders.add(add.displayName)
          }
        })
      }

      if (this.isEntityEnabled) {
        headerRow.push(subCase.entity?.displayName)
        uniqueHeaders.add(subCase.entity?.displayName)
      }

      const addressFields = []
      subCase?.fieldMappings?.addresses?.forEach((address) => {
        Object.values(address).forEach((field) => {
          if (
            field.displayName &&
            field.displayName.trim() &&
            !uniqueHeaders.has(field.displayName)
          ) {
            addressFields.push(field.displayName)
            uniqueHeaders.add(field.displayName)
          }
        })
      })

      addressFields?.forEach((fieldDisplayName) => {
        headerRow.push(fieldDisplayName)
        uniqueHeaders.add(fieldDisplayName)
      })

      tableData.unshift(headerRow)
      return tableData
    },
    totalEMI(emis) {
      if (!emis || !Array.isArray(emis)) return { total: 0, paid: 0, pending: 0, overdue: 0 }
      const totalEmi = emis.length

      const paidStatuses = ['paid', 'minimum amount paid']
      const paidEmi = emis.filter((el) => {
        const status = el.emiStatus?.toLowerCase()
        return status && (paidStatuses.includes(status) || status.includes('paid'))
      }).length

      const today = dayjs().startOf('day')

      // const overdueEmi = emis.filter((emi) => {
      //   const status = emi.emiStatus?.toLowerCase()
      //   const isPaid = status && (paidStatuses.includes(status) || status.includes('paid'))
      //   const dueDate = dayjs(emi.dueDate).startOf('day')
      //   return !isPaid && dueDate.isBefore(today)
      // }).length
      const latePaidEmi = emis.filter((emi) => {
        const status = emi.emiStatus?.toLowerCase()
        return status && status.includes('late')
      }).length
      return {
        total: totalEmi,
        paid: paidEmi,
        pending: totalEmi - (paidEmi + latePaidEmi),
        // overdue: overdueEmi,
        latePaid: latePaidEmi
      }
    },
    totalDisbursed(allDisbursements) {
      if (!allDisbursements || !Array.isArray(allDisbursements)) return 0
      return allDisbursements.reduce((acc, curr) => {
        return acc + (curr.amount || 0)
      }, 0)
    },

    formatDisbursementDetails(allDisbursements) {
      if (!allDisbursements || !Array.isArray(allDisbursements)) return []

      return allDisbursements.map((disbursement, index) => ({
        serialNo: index + 1,
        amount: disbursement.amount || 0,
        date: disbursement.date ? this.$dayjs(disbursement.date).format('DD/MM/YYYY') : 'N/A',
        status: disbursement.status || 'N/A'
      }))
    },

    getStatusColor(status) {
      const statusLower = status?.toLowerCase()
      switch (statusLower) {
        case 'completed':
        case 'success':
        case 'disbursed':
          return 'success'
        case 'pending':
        case 'processing':
          return 'warning'
        case 'failed':
        case 'rejected':
        case 'cancelled':
          return 'error'
        default:
          return 'grey'
      }
    },

    emiAmtStats(emis) {
      if (!emis || !Array.isArray(emis)) return 0
      let totalPrincipalRecovered = 0
      let totalPricipalOutstanding = 0
      let totalInterestExpected = 0
      let totalInterestRecovered = 0
      emis.forEach((el) => {
        totalInterestExpected += el.interestAmount
        const status = el.emiStatus?.toLowerCase()
        const isPaid =
          status === 'paid' ||
          status === 'late' ||
          status === 'late paid' ||
          status?.includes('paid')
        if (isPaid) {
          totalPrincipalRecovered += el.principalAmount
          totalInterestRecovered += el.interestAmount
        } else if (el.emiStatus === 'Minimum Amount Paid') {
          totalInterestRecovered += el.interestAmount
        } else {
          totalPricipalOutstanding += el.principalAmount
        }
      })
      return {
        totalPrincipalRecovered,
        totalPricipalOutstanding,
        totalInterestExpected,
        totalInterestRecovered
      }

      // const firstPaidEmiIndx = emis.findIndex((emi) => emi.emiStatus === 'Paid')
      // if (firstPaidEmiIndx === -1) {
      //   return emis[0]?.outstandingAmount || 0
      // }
      // return firstPaidEmiIndx + 1 < emis.length
      //   ? emis[firstPaidEmiIndx + 1].outstandingAmount
      //   : emis[emis.length - 1]?.outstandingAmount || 0
    },
    lateFeeCalculation(emis) {
      if (!emis || !Array.isArray(emis)) return 0
      let paidLateFee = 0
      let pendingLateFee = 0
      let latePaidCount = 0
      emis.forEach((emi) => {
        const overdueInfo = calculateOverdueDays(
          emi.dueDate,
          emi.emiStatus,
          emi.paidDate,
          emi.emiAmount,
          emi.lateFeeType,
          emi.lateFeeValue
        )
        if (overdueInfo.isLatePaid) {
          latePaidCount++
          paidLateFee += overdueInfo.lateFeeAmount || 0
        } else {
          pendingLateFee += overdueInfo.lateFeeAmount || 0
        }
      })

      return { latePaidCount, paidLateFee, pendingLateFee }
    },
    closeExportDialog() {
      this.exportDialog = false
    },
    employeeFullName(item) {
      if (item && item?.length) {
        const mergedName = item.map((el) => fullName(el))
        return mergedName?.join(', ')
      }
      return ''
    },
    mergedEmployeeId(item) {
      if (item && item?.length) {
        const mergedId = item.map((el) => el.employeeId)
        return mergedId.join(', ')
      }
      return ''
    },
    mergedEmpRole(item) {
      if (item && item?.length) {
        const mergedRole = item.map((el) => el.wfmRole?.role)
        return mergedRole.join(', ')
      }
      return ''
    },
    reportingManager(item) {
      if (item && item.length) {
        const managers = item.map((el) => {
          if (el.reportesTo) {
            return `${el.reportesTo.firstName} ${el.reportesTo.lastName}`
          }
          return ''
        })

        return managers.join(', ')
      }
      return ''
    },
    handleExportKeys() {
      // For loan management, export directly without showing dialog
      if (this.isLoanManagement) {
        this.exportLoanManagementData()
        return
      }

      // For other use cases, show the export dialog
      const exportOptions = this.prepareExportData(this.cases)
      if (exportOptions?.length > 0) {
        const allKeys = new Set()
        exportOptions.forEach((row) => {
          Object.keys(row).forEach((key) => allKeys.add(key))
        })
        const exportKeysArray = Array.from(allKeys)
        this.exportColumnHeaders = exportKeysArray.map((key) => ({
          text: key,
          value: key
        }))
      } else {
        this.exportColumnHeaders = []
      }
      this.exportDialog = true
    },

    prepareExportData(dataCopy) {
      const configLang = this.caseConfig.fieldMappings

      return dataCopy.flatMap((item, index) => {
        const {
          taskDescription,
          taskTitle,
          caseNo,
          bkt,
          pos,
          emiAmount,
          tenure,
          assignedTo,
          typeOfLoan,
          crn,
          customerFullName,
          productInfo,
          status,
          caseOutcomeStatus
        } = configLang || {}

        const descriptionDisplayName = taskDescription?.displayName || 'Description'
        const taskDisplayName = taskTitle?.displayName || 'Task Title'
        const tasks = [...(item?.tasks || [])].reverse()

        const mergeRemarks = (type) =>
          tasks
            ?.filter((task) => (type === 'call' ? task.callInfo?.length : !task.callInfo))
            .map(
              ({ actualStartDateTime, meetingDetails }) =>
                `${this.$dayjs(actualStartDateTime).format('DD/MM/YYYY hh:mm:ss A')} : ${
                  meetingDetails?.meetingNotes
                }`
            )
            .join('  ||  ')

        const mergeVisitRemarks = mergeRemarks('visit')
        const mergeCallRemarks = mergeRemarks('call')

        const taskFields = tasks?.reduce((fields, task, taskIndex) => {
          const {
            actualStartDateTime,
            actualEndDateTime,
            taskTitle,
            taskDescription,
            taskStatus,
            meetingDetails,
            additionalInfo
          } = task

          return {
            ...fields,
            [`Merged ${this.tabText.visitTable?.displayName || 'Visit'} Remarks`]:
              mergeVisitRemarks,
            'Merged Call Remarks': mergeCallRemarks,
            [`${this.tabText.visitTable?.displayName || 'Visit'} ${taskIndex + 1}`]: taskTitle,
            [`${descriptionDisplayName}`]: taskDescription,
            [`${this.tabText.visitTable?.displayName || 'Visit'} Start ${taskIndex + 1}`]:
              actualStartDateTime
                ? this.$dayjs(actualStartDateTime).format('DD/MM/YYYY hh:mm A')
                : '',
            [`${this.tabText.visitTable?.displayName || 'Visit'} End ${taskIndex + 1}`]:
              actualEndDateTime ? this.$dayjs(actualEndDateTime).format('DD/MM/YYYY hh:mm A') : '',
            [`Next ${this.tabText.visitTable?.displayName || 'Visit'} date of ${
              this.tabText.visitTable?.displayName || 'Visit'
            } ${taskIndex + 1}`]: actualEndDateTime
              ? this.$dayjs(meetingDetails?.nextTaskScheduledOn).format('DD/MM/YYYY hh:mm A')
              : '',
            [`${this.tabText.visitTable?.displayName || 'Visit'} Status ${taskIndex + 1}`]:
              taskStatus,
            [`Meeting Note ${taskIndex + 1}`]: meetingDetails?.meetingNotes,
            [`Meeting Outcome ${taskIndex + 1}`]: meetingDetails?.meetingOutCome,
            [`${meetingDetails?.meetingOutCome || ''} Amount ${taskIndex + 1}`]:
              meetingDetails?.fundCollected,
            [`Image Link ${taskIndex + 1}`]: additionalInfo?.selfie
              ? `${this.awsBaseUrl}${additionalInfo.selfie.doc_physical_path}`
              : '',
            [`Audio Link ${taskIndex + 1}`]: additionalInfo?.audio
              ? `${this.awsBaseUrl}${additionalInfo.audio.doc_physical_path}`
              : ''
          }
        }, {})

        const additionalData = Object.entries(item?.additionalColumns || {}).reduce(
          (columns, [key, value]) => {
            columns[key] = value || ''
            return columns
          },
          {}
        )

        // Repeat rows based on assignedTo
        return (item.assignedTo || [{}]).map((employee, empIndex) => ({
          'S.No': index + 1,
          [`${caseNo?.displayName || 'Case No'}`]: item.caseNo || '',
          [`${bkt?.displayName || 'BKT'}`]: item.bkt || '',
          [`${pos?.displayName || 'POS'}`]: item.pos || '',
          [`${emiAmount?.displayName || 'EMI Amount'}`]: item.emiAmount || '',
          [`${tenure?.displayName || 'Tenure'}`]: item.tenure || '',
          'Employee Name': employee
            ? `${employee.firstName || ''} ${employee.lastName || ''}`.trim()
            : '',
          'Employee Id': employee?.employeeId || '',
          Role: employee?.wfmRole?.role || '',
          [`${assignedTo?.displayName || 'FOS NUMBER'}`]: employee?.mobile || '',
          'Employee Reporting Manager': employee?.reportesTo
            ? `${employee.reportesTo.firstName || ''} ${employee.reportesTo.lastName || ''}`.trim()
            : '',
          [`${typeOfLoan?.displayName || 'Type of Loan'}`]: item.typeOfLoan || '',
          [`${crn?.displayName || 'CRN'}`]: item.customer?.crn || '',
          [`${customerFullName?.displayName || 'Customer Full Name'}`]:
            item.customer?.customerFullName || '',
          ...(this.isEntityEnabled
            ? {
                [`${this.caseConfig?.entity?.displayName}`]: item.entity?.entityName || ''
              }
            : {}),
          [`${productInfo?.displayName || 'Product Info'}`]: item.productInfo,
          'Start On': item.caseStartDate
            ? this.$dayjs(item.caseStartDate).format('DD/MM/YYYY hh:mm A')
            : '',
          'Closed On': item.caseEndDate
            ? this.$dayjs(item.caseEndDate).format('DD/MM/YYYY hh:mm A')
            : '',
          TAT: this.calculateTAT(item),
          [`${status?.displayName || 'Status'}`]: item.status,
          [`${caseOutcomeStatus?.displayName || 'Outcome'}`]: item.userDefinedCaseStatus || '',
          ...taskFields,
          ...additionalData
        }))
      })
    },
    async exportCases(selectedKeys) {
      try {
        const chunkSize = 50
        this.exportLoading = true
        const totalPages = Math.ceil(this.totalItems / chunkSize)

        const promises = []

        for (let index = 0; index < totalPages; index++) {
          const filterParams = this.filterParams()

          const params = {
            limit: chunkSize,
            page: index + 1,
            ...filterParams
          }
          const promise = this.$axios.get(axiosUrl.cases.getCases, {
            params
          })
          promises.push(promise)
        }

        const responses = await Promise.all(promises)

        const data = responses.reduce((acc, response) => {
          return acc.concat(response.data.collections)
        }, [])
        const XLSX = require('xlsx')
        const dataCopy = [...data]
        const restructuredData = this.prepareExportData(dataCopy)
        const filteredData = restructuredData.map((row) => {
          const filteredRow = {}
          selectedKeys.forEach((key) => {
            filteredRow[key] = row[key]
          })
          return filteredRow
        })
        const ws = XLSX.utils.json_to_sheet(filteredData)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Task Data')
        const file_date = new Date().toISOString()
        const filename = `${this.caseConfig?.name}_${file_date}.xlsx`
        XLSX.writeFile(wb, filename)
      } catch (error) {
        console.error('Error exporting data:', error)
      } finally {
        this.exportLoading = false
      }
    },

    async exportLoanManagementData() {
      try {
        const chunkSize = 50
        this.exportLoading = true
        const totalPages = Math.ceil(this.totalItems / chunkSize)

        const promises = []

        for (let index = 0; index < totalPages; index++) {
          const filterParams = this.filterParams()

          const params = {
            // limit: chunkSize,
            // page: index + 1
            ...filterParams
          }
          const promise = this.$axios.get(
            `${axiosUrl.cases.getLoanCases}=68724b5d8e15f2046ee23837&page=1&limit=50`,
            {
              params
            }
          )
          promises.push(promise)
        }

        const responses = await Promise.all(promises)

        const data = responses.reduce((acc, response) => {
          return acc.concat(response.data.loans)
        }, [])

        const XLSX = require('xlsx')
        const restructuredData = this.prepareLoanManagementExportData(data)

        const ws = XLSX.utils.json_to_sheet(restructuredData)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Loan Management Data')
        const file_date = new Date().toISOString()
        const filename = `Loan_Management_${file_date}.xlsx`
        XLSX.writeFile(wb, filename)
      } catch (error) {
        console.error('Error exporting loan management data:', error)
      } finally {
        this.exportLoading = false
      }
    },

    prepareLoanManagementExportData(data) {
      return data.map((item, index) => {
        const loanDetails = item.loanDetails || {}
        const customer = item.customer || {}
        const loanWorkflow = loanDetails.loanWorkflow || {}
        const emiSchedule = loanDetails.emiSchedule || []
        const partner = loanDetails.partner || {}

        // Basic loan information
        const baseData = {
          'S.No': index + 1,
          'Case No': item.caseNo || '',
          'Case Type': item.caseType || '',
          'Use Case': item.useCase || '',
          'Sub Case': item.subCase || '',
          Status: item.status || '',

          // Customer Information
          'Customer CRN': customer.crn || '',
          'Customer Full Name': customer.customerFullName || '',
          'Customer Gender': customer.gender || '',

          // Loan Details
          'Application Number': loanDetails.applicationNumber || '',
          'Type of Loan': loanDetails.typeOfLoan || '',
          'Loan Amount Requested': loanDetails.loanAmountRequested || '',
          'Loan Amount Sanctioned': loanDetails.loanAmountSanctioned || '',
          'Total Amount Disbursed': loanDetails.totalAmountDisbursed || 0,
          'Type of Interest': loanDetails.typeOfInterest || '',
          'Interest Rate': loanDetails.interestRate || '',
          'Tenure Type': loanDetails.tenureType || '',
          Tenure: loanDetails.tenure || '',
          'EMI Start Date': loanDetails.emiStartDate
            ? this.$dayjs(loanDetails.emiStartDate).format('DD/MM/YYYY')
            : '',
          'Total EMI Paid': loanDetails.totalEmiPaid || 0,
          'Overdue Amount': loanDetails.overdueAmount || 0,
          'Processing Fee': loanDetails.processingFee || 0,
          'Late Payment Fee': loanDetails.latePaymentFee || 0,
          'Prepayment Charges': loanDetails.prepaymentCharges || 0,
          'Advisory Fee': loanDetails.advisoryFee || 0,
          'Annual Processing Rate': loanDetails.annualProcessingRate || 0,
          'Debt Service Ratio': loanDetails.debtServiceRatio || 0,
          'Sanction Date': loanDetails.sanctionDate
            ? this.$dayjs(loanDetails.sanctionDate).format('DD/MM/YYYY')
            : '',
          'KYC Status': loanDetails.kycStatus || '',
          'Current Status': loanDetails.currentStatus || '',

          // Partner Information
          'Partner Name': partner.partnerName || '',
          'Partner Status': partner.partnerStatus || '',

          // EMI Schedule Summary
          'Total EMI Count': emiSchedule.length || 0,
          'Pending EMI Count': emiSchedule.filter((emi) => !emi.isPaid).length || 0,
          'Paid EMI Count': emiSchedule.filter((emi) => emi.isPaid).length || 0
        }

        // Add workflow steps information
        if (loanWorkflow.steps && loanWorkflow.steps.length > 0) {
          loanWorkflow.steps.forEach((step, stepIndex) => {
            const stepPrefix = `Step ${stepIndex + 1}`
            baseData[`${stepPrefix} - ID`] = step.stepId || ''
            baseData[`${stepPrefix} - Name`] = step.name || ''
            baseData[`${stepPrefix} - Status`] = step.status || ''
            baseData[`${stepPrefix} - Started At`] = step.startedAt
              ? this.$dayjs(step.startedAt).format('DD/MM/YYYY hh:mm A')
              : ''
            baseData[`${stepPrefix} - Completed At`] = step.completedAt
              ? this.$dayjs(step.completedAt).format('DD/MM/YYYY hh:mm A')
              : ''
            baseData[`${stepPrefix} - Due Date`] = step.dueDate
              ? this.$dayjs(step.dueDate).format('DD/MM/YYYY hh:mm A')
              : ''
            baseData[`${stepPrefix} - Remarks`] = step.remarks || ''
            baseData[`${stepPrefix} - Rejection Reason`] = step.rejectionReason || ''

            // Add requirements for each step
            if (step.requirements && step.requirements.length > 0) {
              step.requirements.forEach((req, reqIndex) => {
                const reqPrefix = `${stepPrefix} Req ${reqIndex + 1}`
                baseData[`${reqPrefix} - Type`] = req.type || ''
                baseData[`${reqPrefix} - Name`] = req.name || ''
                baseData[`${reqPrefix} - Status`] = req.status || ''
                baseData[`${reqPrefix} - Submitted At`] = req.submittedAt
                  ? this.$dayjs(req.submittedAt).format('DD/MM/YYYY hh:mm A')
                  : ''
                baseData[`${reqPrefix} - Document URL`] = req.documentUrl || ''
                baseData[`${reqPrefix} - Remarks`] = req.remarks || ''
              })
            }
          })
        }

        // Add EMI schedule details
        emiSchedule.forEach((emi, emiIndex) => {
          const emiPrefix = `EMI ${emiIndex + 1}`
          baseData[`${emiPrefix} - Due Date`] = emi.dueDate
            ? this.$dayjs(emi.dueDate).format('DD/MM/YYYY')
            : ''
          baseData[`${emiPrefix} - EMI Amount`] = emi.emiAmount || 0
          baseData[`${emiPrefix} - Principal Amount`] = emi.principalAmount || 0
          baseData[`${emiPrefix} - Interest Amount`] = emi.interestAmount || 0
          baseData[`${emiPrefix} - Outstanding Amount`] = emi.outstandingAmount || 0
          baseData[`${emiPrefix} - Is Paid`] = emi.isPaid ? 'Yes' : 'No'
          baseData[`${emiPrefix} - Status`] = emi.emiStatus || ''

          // Add activities for each EMI
          if (emi.activities && emi.activities.length > 0) {
            emi.activities.forEach((activity, actIndex) => {
              const actPrefix = `${emiPrefix} Activity ${actIndex + 1}`
              baseData[`${actPrefix} - Type`] = activity.activityType || ''
              baseData[`${actPrefix} - Document`] = activity.activityDoc || ''
              baseData[`${actPrefix} - Status`] = activity.status || ''
              baseData[`${actPrefix} - Updated At`] = activity.updatedAt
                ? this.$dayjs(activity.updatedAt).format('DD/MM/YYYY hh:mm A')
                : ''
            })
          }
        })

        // Add loan disbursements
        if (loanDetails.loanDisbursements && loanDetails.loanDisbursements.length > 0) {
          loanDetails.loanDisbursements.forEach((disbursement, disbIndex) => {
            const disbPrefix = `Disbursement ${disbIndex + 1}`
            baseData[`${disbPrefix} - Amount`] = disbursement.amount || 0
            baseData[`${disbPrefix} - Date`] = disbursement.date
              ? this.$dayjs(disbursement.date).format('DD/MM/YYYY')
              : ''
            baseData[`${disbPrefix} - Status`] = disbursement.status || ''
            baseData[`${disbPrefix} - Remarks`] = disbursement.remarks || ''
            baseData[`${disbPrefix} - Reference No`] = disbursement.referenceNo || ''
            baseData[`${disbPrefix} - Payment Mode`] = disbursement.paymentMode || ''

            if (disbursement.bankDetails) {
              baseData[`${disbPrefix} - Bank Name`] = disbursement.bankDetails.bankName || ''
              baseData[`${disbPrefix} - Account Number`] =
                disbursement.bankDetails.accountNumber || ''
              baseData[`${disbPrefix} - IFSC Code`] = disbursement.bankDetails.ifscCode || ''
            }
          })
        }

        return baseData
      })
    },
    async fetchBranches() {
      try {
        const response = await this.$axios.get(axiosUrl.branches.getBranches)
        this.branches = response.data.branches
      } catch (error) {
        console.error('Error fetching branches:', error)
      }
    },
    async fetchTeams() {
      try {
        const response = await this.$axios.get(axiosUrl.teams.getTeams)
        this.teams = response.data.teams || []
      } catch (error) {
        console.error('Error fetching branches:', error)
      }
    },
    calculateTAT(item) {
      const caseStart = item.caseStartDate
      const caseEnd = item.caseEndDate
      if (caseEnd) {
        return dayHourFormat(caseStart, caseEnd)
      }
      return '-'
    },
    findBranch(item) {
      if (item) {
        const branch = this.branches.find((el) => el._id === item.branch)
        return branch?.branchName || '-'
      }
      return '-'
    },
    findTeam(id) {
      if (id) {
        const team = this.teams?.find((el) => el?._id === id)
        return team?.teamName || '-'
      }
      return '-'
    },
    casesEdit(item) {
      this.openEditCase = true
      this.editCase = item
    },
    employeeMobile(arr) {
      if (arr && arr.length) {
        const mergedNumber = arr.map((el) => {
          return el.mobile
        })
        return mergedNumber.join(', ') || '-'
      }

      return 'N/A'
    },
    toolTipName(arr) {
      if (arr && arr.length) {
        const mergedName = arr.map((el) => {
          return `${el.firstName} ${el.lastName}`
        })
        return mergedName.join(', ') || 'Un-assigned'
      }
      return arr?.firstName || 'Un-assigned'
    },
    addCaseTask(item) {
      this.editCase = item
      this.openAddCaseTask = true
    },
    uploadCases(text) {
      this.caseAction = text
      this.openUploadCases = true
    },
    closeUpload() {
      this.openUploadCases = false
    },
    caseDetails(item) {
      this.openCaseDetails = true
      this.selectedCase = item
    },
    async handleSelectCaseById(caseId) {
      try {
        const response = await this.$axios.get(`/collection/case/${caseId}`)
        const caseData = response.data && (response.data.case || response.data.data)
        if (caseData) {
          this.caseDetails(caseData)
        }
      } catch (error) {
        console.error('Error fetching linked case details:', error)
        this.$toast.error('Failed to load linked case details')
      }
    },

    formatDate(dateString) {
      return this.$dayjs(dateString).format('YYYY-MM-DD')
    },
    visibleDate(date) {
      return this.$dayjs(date).format('DD-MM-YYYY')
    },
    cancel() {
      this.showConfirmationDialog = false
      this.fetchCases()
    },
    cancelVisitUpdate() {
      this.showUpdateVisitDialog = false
      this.visitAssignedTo = null
      this.visitId = null
      this.newAssignedTo = null
      this.fetchCases()
    },
    handleAdded() {
      this.openEditCase = false
      this.openAddCaseTask = false
      this.fetchCases()
    },
    async handleEntity(val) {
      this.searchValue = val
      await this.getAllEntities()
    },
    closeDialog() {
      this.openCaseDetails = false
      if (this.$route.query.caseNo) {
        const query = { ...this.$route.query, caseNo: undefined }
        this.$router.replace({ query })
      }
      this.fetchCases()
    },
    addNewCase() {
      this.editCase = null
      this.openEditCase = true
    },
    convertTitle(str) {
      if (str) {
        return convertToTitleCase(str)
      }
      return '-'
    },
    handleInput: debounce(async function () {
      this.options.page = 1
      await this.fetchCases()
    }, 800),
    updateSelectedCount() {
      this.selectedCount = [
        this.filterAssignedTo,
        this.filterCaseOutcome,
        this.filterStatus,
        this.formattedDate,
        this.filterPartner,
        this.filterTag
      ].filter((item) => item !== null && item !== '' && item.length > 0).length
    },
    handleChange() {
      this.handleInput()
      this.updateSelectedCount()
    },
    handleShowDialog(label) {
      this.dialogLable = label
      this.showDialog = !this.showDialog
      if (this.showDialog === false) {
        this.fetchCases()
      }
    },
    handleShowDialogDelete(label) {
      this.dialogLable = label
      this.showDeleteDialog = true
      this.deleteCustomer = false
    },
    closeDeleteDialog() {
      this.showDeleteDialog = false
      this.fetchCases()
    },

    handleShowDialogDeleteAll(label) {
      this.dialogLable = label
      this.showDeleteAllDialog = !this.showDeleteAllDialog
      if (this.showDeleteAllDialog === false) {
        this.fetchCases()
      }
    },
    handleDeleteSelected() {
      this.selected = []
    },
    handleUnassignedCases() {
      this.filterAssignedTo = 'null'
      this.handleChange()
    },
    handleNoOutcome() {
      // this.filterCaseOutcome = 'null'
      this.handleChange()
    },
    handleBranchChange() {
      this.filterTeam = null
      this.handleChange()
    },
    isTeamDisabled(team) {
      if (!this.filterBranch) return true
      return team.branch !== this.filterBranch
    },
    clearOutcomeFilter() {
      this.filterCaseOutcome = null
      this.handleChange()
    },
    async handleSelect() {
      this.filterCase = ''
      await this.fetchCases()
    },

    // Loan bulk upload methods
    openLoanBulkUpload() {
      this.openLoanBulkUploadDialog = true
    },

    closeLoanBulkUpload() {
      this.openLoanBulkUploadDialog = false
    },

    handleLoanUploadComplete() {
      // Refresh the table data when upload is complete
      this.fetchCases()
    },
    routeToPartner(item) {
      const partnerName = item.loanDetails?.partner?.partnerName || ''
      if (partnerName) {
        const url = this.$router.resolve(`/workforce/partners?search=${partnerName}`).href
        window.open(url, '_blank')
      }
    },
    routeToCustomer(item) {
      if (item.customer) {
        this.currentCustomer = item.customer
        this.openCustomerDetails = true
      }
    },
    handleAssignLocationClick() {
      if (this.selected.length === 0) {
        this.$toast.warning('Please select at least one record')
        return
      }
      // Directly open the assign location dialog with both team and branch selections
      this.handleShowDialog(UpdateCaseDialogLabels.ASSIGN_LOCATION)
    },
    async fetchDepartments() {
      try {
        const response = await this.$axios.get(axiosUrl.departments.getDepartments)
        this.departments = response.data.departments || []
      } catch (error) {
        console.error('Error fetching departments:', error)
      }
    },
    async fetchDesignations() {
      try {
        const response = await this.$axios.get(axiosUrl.designations.getDesignations)
        this.designations = response.data.designations || []
      } catch (error) {
        console.error('Error fetching designations:', error)
      }
    },
    handleAssignDepartmentClick() {
      if (this.selected.length === 0) {
        this.$toast.warning('Please select at least one record')
        return
      }
      this.handleShowDialog(UpdateCaseDialogLabels.ASSIGN_DEPARTMENT)
    }
  },
  async mounted() {
    if (this.$route.query.caseNo) {
      this.filterCase = this.$route.query.caseNo
    }
    if (this.$route.query.userDefinedCaseStatus) {
      this.filterCaseOutcome = this.$route.query.userDefinedCaseStatus
      this.updateSelectedCount()
    }
    await this.fetchTeams()
    await this.fetchBranches()
    await this.fetchDepartments()
    await this.fetchDesignations()
    this.$emit('component-mounted')
  }
}
</script>
<style scoped>
.v-data-table-header th span {
  font-size: 14px;
  color: #262626;
  font-weight: 400;
}

.v-data-table >>> .v-data-table__wrapper > table > tbody > tr > td > .v-data-table__checkbox {
  text-align: center;
}
</style>

<style scoped>
.wfm-search-fields.v-text-field--outlined > .v-input__control > .v-input__slot {
  min-height: 40px !important;
}
.unassignedOptions {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: #ffffff;
}
.noOutcomeOptions {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: #ffffff;
}
.v-list {
  padding-top: 0px !important;
}
.custom-input-group {
  display: flex;
  align-items: center;
  padding: 2px;
  background-color: white;
  border-radius: 5px;
}

.select-input {
  flex-grow: 1;
  width: 15%;
  font-size: smaller;
  border-top-right-radius: 0px;
  border-bottom-right-radius: 0px;
}

.text-input {
  border-top-left-radius: 0px;
  border-bottom-left-radius: 0px;
}

/* Disbursement styles */
.disbursement-row {
  font-size: 0.875rem;
  line-height: 1.2;
}

.disbursement-serial {
  font-weight: 600;
  color: #666;
}
.table-radius {
  border-radius: 0px !important;
}

/* .disbursement-amount {
  font-weight: 800;
} */

.disbursement-date {
  font-size: 0.8rem;
  color: #666;
}
.font-title {
  font-size: 0.875rem;
  color: #000;
  margin-bottom: 0px !important;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/*.v-data-table >>> .v-data-table__wrapper > table > thead > tr > th {
  font-size: 0.875rem;
  color: #000;
}*/
.input-field {
  font-size: 0.875rem !important;
}
/* .table-font {
  font-size: 0.8rem !important;
} */
</style>

<style scoped>
.ecom-sidebar-header {
  border-right: 1px solid #e9e9ed;
  flex: 0 0 260px;
  max-width: 260px;
}
.ecom-sidebar {
  height: calc(100vh - 200px);
  overflow-y: auto;
}
.ecom-table-container {
  height: calc(100vh - 200px);
  overflow-y: auto;
  overflow-x: auto;
  min-width: 0;
}
.ecom-table {
  min-height: 100%;
}
.border-b {
  border-bottom: 1px solid #e9e9ed;
}

@media (max-width: 959px) {
  .ecom-sidebar {
    flex: 0 0 100%;
    max-width: 100%;
    height: 35vh;
    border-right: none !important;
    border-bottom: 1px solid #e9e9ed;
  }
  .ecom-table-container {
    height: 50vh;
    padding-left: 0 !important;
  }
}
</style>
